import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  AcceptMemberInviteRequest,
  InviteMemberRequest,
  OrganizerSessionResponse,
  TeamMemberResponse,
  UpdateMemberRoleRequest,
} from "@ticketnjoy/types";

import { ApiError, apiClient } from "@/lib/api-client";
import { meQueryKey } from "@/features/organizers/api";

export const teamMembersQueryKey = ["team", "members"] as const;

export function useTeamMembersQuery() {
  return useQuery<TeamMemberResponse[], ApiError>({
    queryKey: teamMembersQueryKey,
    queryFn: () => apiClient<TeamMemberResponse[]>("/organizers/me/members"),
    staleTime: 30 * 1000,
  });
}

export function useInviteMemberMutation() {
  const qc = useQueryClient();
  return useMutation<TeamMemberResponse, Error, InviteMemberRequest>({
    mutationFn: (body) =>
      apiClient<TeamMemberResponse>("/organizers/me/members/invite", {
        method: "POST",
        body,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: teamMembersQueryKey });
    },
  });
}

export function useResendInviteMutation(memberId: string) {
  const qc = useQueryClient();
  return useMutation<TeamMemberResponse, Error, void>({
    mutationFn: () =>
      apiClient<TeamMemberResponse>(`/organizers/me/members/${memberId}/resend-invite`, {
        method: "POST",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: teamMembersQueryKey });
    },
  });
}

export function useUpdateMemberRoleMutation(memberId: string) {
  const qc = useQueryClient();
  return useMutation<TeamMemberResponse, Error, UpdateMemberRoleRequest>({
    mutationFn: (body) =>
      apiClient<TeamMemberResponse>(`/organizers/me/members/${memberId}/role`, {
        method: "PATCH",
        body,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: teamMembersQueryKey });
    },
  });
}

export function useDisableMemberMutation(memberId: string) {
  const qc = useQueryClient();
  return useMutation<TeamMemberResponse, Error, void>({
    mutationFn: () =>
      apiClient<TeamMemberResponse>(`/organizers/me/members/${memberId}/disable`, {
        method: "POST",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: teamMembersQueryKey });
    },
  });
}

export function useEnableMemberMutation(memberId: string) {
  const qc = useQueryClient();
  return useMutation<TeamMemberResponse, Error, void>({
    mutationFn: () =>
      apiClient<TeamMemberResponse>(`/organizers/me/members/${memberId}/enable`, {
        method: "POST",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: teamMembersQueryKey });
    },
  });
}

export function useDeleteMemberMutation(memberId: string) {
  const qc = useQueryClient();
  return useMutation<void, Error, void>({
    mutationFn: () =>
      apiClient<void>(`/organizers/me/members/${memberId}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: teamMembersQueryKey });
    },
  });
}

export function useAcceptInviteMutation() {
  const qc = useQueryClient();
  return useMutation<OrganizerSessionResponse, Error, AcceptMemberInviteRequest>({
    mutationFn: (body) =>
      apiClient<OrganizerSessionResponse>("/organizers/invites/accept", {
        method: "POST",
        body,
      }),
    onSuccess: (data) => {
      qc.setQueryData(meQueryKey, data);
    },
  });
}
